/*
 * AdminRevenue — verqo Revenue Mission Control
 *
 * Single page that answers: "Where is every dollar verqo earns going,
 * and how do we withdraw it to our corporate bank?"
 *
 * Sections (top → bottom):
 *   1. KPI strip — Lifetime · YTD · MTD · 30d · Momentum · Treasury balance
 *   2. Treasury card — platform sub-ledger + "Sweep to bank" + payment methods
 *   3. Revenue stream tiles — one per source, lifetime + MTD
 *   4. 12-month stacked area chart by source
 *   5. Top seller contributors (revenue attribution)
 *   6. Filterable ledger with CSV export
 */
import { useEffect, useMemo, useState } from "react";
import {
  DollarSign, Wallet, Banknote, TrendingUp, TrendingDown, ArrowDownToLine,
  Plus, Trash2, Star, Download, RefreshCw, Loader2, Search,
  Package, Receipt, Shield, Truck, CreditCard, AppWindow,
  Sparkles, Percent, ArrowUpRight, ArrowDownRight, Building2, X,
} from "lucide-react";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { PageHeader } from "../../components/PortalShell";
import { toast } from "sonner";

const money = (n) =>
  `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const moneyShort = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
};
const pct = (n, digits = 1) =>
  `${(Number(n) || 0).toFixed(digits)}%`;

const SOURCE_ICON = {
  dropship:           Package,
  factoring:          Receipt,
  insurance:          Shield,
  shipping:           Truck,
  wallet_topup_card:  CreditCard,
  wallet_topup_ach:   Banknote,
  wallet_topup_wire:  Building2,
  app_subscription:   AppWindow,
  extension_fee:      Percent,
  wallet_float:       Sparkles,
};

const SOURCE_TONE = {
  dropship:           "from-emerald-50 to-emerald-100 text-emerald-700 border-emerald-200",
  factoring:          "from-indigo-50 to-indigo-100 text-indigo-700 border-indigo-200",
  insurance:          "from-sky-50 to-sky-100 text-sky-700 border-sky-200",
  shipping:           "from-orange-50 to-orange-100 text-orange-700 border-orange-200",
  wallet_topup_card:  "from-rose-50 to-rose-100 text-rose-700 border-rose-200",
  wallet_topup_ach:   "from-amber-50 to-amber-100 text-amber-700 border-amber-200",
  wallet_topup_wire:  "from-violet-50 to-violet-100 text-violet-700 border-violet-200",
  app_subscription:   "from-fuchsia-50 to-fuchsia-100 text-fuchsia-700 border-fuchsia-200",
  extension_fee:      "from-yellow-50 to-yellow-100 text-yellow-700 border-yellow-200",
  wallet_float:       "from-teal-50 to-teal-100 text-teal-700 border-teal-200",
};

const SOURCE_BAR_COLOR = {
  dropship:           "#10b981",
  factoring:          "#6366f1",
  insurance:          "#0ea5e9",
  shipping:           "#f97316",
  wallet_topup_card:  "#f43f5e",
  wallet_topup_ach:   "#f59e0b",
  wallet_topup_wire:  "#8b5cf6",
  app_subscription:   "#d946ef",
  extension_fee:      "#eab308",
  wallet_float:       "#14b8a6",
};


export default function AdminRevenue() {
  const [overview, setOverview] = useState(null);
  const [streams, setStreams] = useState([]);
  const [series, setSeries] = useState(null);
  const [topSellers, setTopSellers] = useState([]);
  const [treasury, setTreasury] = useState(null);
  const [ledger, setLedger] = useState({ rows: [], count: 0, summary: { fee: 0, gross: 0 } });
  const [loading, setLoading] = useState(true);
  const [sweepOpen, setSweepOpen] = useState(false);
  const [methodOpen, setMethodOpen] = useState(false);

  // Ledger filters
  const [fSource, setFSource] = useState("");
  const [fQuery, setFQuery] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");

  const refreshAll = async () => {
    setLoading(true);
    try {
      const [ov, st, sr, ts, tr] = await Promise.all([
        api.get("/admin/revenue/overview").then(r => r.data),
        api.get("/admin/revenue/streams").then(r => r.data),
        api.get("/admin/revenue/timeseries?months=12").then(r => r.data),
        api.get("/admin/revenue/top-sellers?limit=12").then(r => r.data),
        api.get("/admin/treasury").then(r => r.data),
      ]);
      setOverview(ov);
      setStreams(st.streams || []);
      setSeries(sr);
      setTopSellers(ts.rows || []);
      setTreasury(tr);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load verqo revenue");
    } finally {
      setLoading(false);
    }
  };

  const refreshLedger = async () => {
    try {
      const params = new URLSearchParams();
      if (fSource) params.set("source", fSource);
      if (fQuery)  params.set("q",      fQuery);
      if (fFrom)   params.set("date_from", fFrom);
      if (fTo)     params.set("date_to",   fTo);
      params.set("limit", "200");
      const r = await api.get(`/admin/revenue/ledger?${params}`).then(x => x.data);
      setLedger(r);
    } catch (e) {
      toast.error("Failed to load ledger");
    }
  };

  useEffect(() => { refreshAll(); }, []);
  useEffect(() => { refreshLedger(); /* eslint-disable-next-line */ }, [fSource]);

  const exportCsv = async () => {
    const params = new URLSearchParams();
    if (fSource) params.set("source", fSource);
    if (fFrom)   params.set("date_from", fFrom);
    if (fTo)     params.set("date_to",   fTo);
    try {
      const r = await api.get(`/admin/revenue/export.csv?${params}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `verqo-revenue-${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  if (loading || !overview) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin text-foreground/40" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-revenue">
      <PageHeader
        title="verqo Revenue · Mission Control"
        subtitle="Every dollar verqo earns across all 10 revenue streams · live treasury balance · withdraw to corporate bank"
      />

      {/* ───── KPI strip ───── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi
          label="Lifetime revenue"
          value={moneyShort(overview.lifetime.fee)}
          hint={`${overview.lifetime.count.toLocaleString()} transactions`}
          icon={<DollarSign className="w-3.5 h-3.5"/>}
          tone="ink"
          testid="kpi-lifetime"
        />
        <Kpi
          label="YTD revenue"
          value={moneyShort(overview.ytd.fee)}
          hint={`${overview.ytd.count.toLocaleString()} txns`}
          icon={<TrendingUp className="w-3.5 h-3.5"/>}
          testid="kpi-ytd"
        />
        <Kpi
          label="This month"
          value={moneyShort(overview.mtd.fee)}
          hint={`${overview.mtd.count.toLocaleString()} txns`}
          icon={<DollarSign className="w-3.5 h-3.5"/>}
          testid="kpi-mtd"
        />
        <Kpi
          label="Last 30 days"
          value={moneyShort(overview.last_30d.fee)}
          hint={`vs ${moneyShort(overview.prev_30d.fee)} prev 30d`}
          icon={<TrendingUp className="w-3.5 h-3.5"/>}
          testid="kpi-30d"
        />
        <Kpi
          label="Momentum (30d MoM)"
          value={pct(overview.momentum_pct, 1)}
          hint={overview.momentum_pct >= 0 ? "vs previous 30 days" : "down from previous 30 days"}
          icon={overview.momentum_pct >= 0 ? <ArrowUpRight className="w-3.5 h-3.5"/> : <ArrowDownRight className="w-3.5 h-3.5"/>}
          tone={overview.momentum_pct >= 0 ? "good" : "warn"}
          testid="kpi-momentum"
        />
        <Kpi
          label="Treasury balance"
          value={moneyShort(overview.treasury.balance)}
          hint="Available to sweep"
          icon={<Wallet className="w-3.5 h-3.5"/>}
          tone="ink"
          testid="kpi-treasury"
        />
      </div>

      {/* ───── Treasury card ───── */}
      <TreasuryCard
        treasury={treasury}
        onSweep={() => setSweepOpen(true)}
        onAddMethod={() => setMethodOpen(true)}
        onRefresh={refreshAll}
      />

      {/* ───── Revenue stream tiles ───── */}
      <div>
        <SectionTitle title="Revenue streams" subtitle="One tile per source · sorted by lifetime contribution" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {streams.map(s => (
            <StreamTile key={s.source} stream={s} />
          ))}
        </div>
      </div>

      {/* ───── Stacked chart ───── */}
      <div>
        <SectionTitle title="Revenue over time" subtitle="Last 12 months · stacked by stream" />
        <StackedChart series={series} />
      </div>

      {/* ───── Top sellers ───── */}
      <div>
        <SectionTitle
          title="Top revenue-contributing sellers"
          subtitle="Where verqo's fees originate · lifetime"
        />
        <TopSellersTable rows={topSellers} />
      </div>

      {/* ───── Ledger with filters + export ───── */}
      <div>
        <SectionTitle
          title="Revenue ledger"
          subtitle={`${ledger.count} rows · ${money(ledger.summary.fee)} in fees on ${money(ledger.summary.gross)} gross`}
          right={
            <Button size="sm" variant="outline" onClick={exportCsv} data-testid="export-csv">
              <Download className="w-4 h-4 mr-1.5" />
              Export CSV
            </Button>
          }
        />
        <LedgerFilters
          source={fSource} setSource={setFSource}
          query={fQuery}   setQuery={setFQuery}
          from={fFrom}     setFrom={setFFrom}
          to={fTo}         setTo={setFTo}
          onApply={refreshLedger}
          onClear={() => { setFSource(""); setFQuery(""); setFFrom(""); setFTo(""); setTimeout(refreshLedger, 0); }}
        />
        <LedgerTable rows={ledger.rows} />
      </div>

      {/* ───── Dialogs ───── */}
      <SweepDialog
        open={sweepOpen}
        onOpenChange={setSweepOpen}
        treasury={treasury}
        onDone={refreshAll}
      />
      <AddMethodDialog
        open={methodOpen}
        onOpenChange={setMethodOpen}
        onDone={refreshAll}
      />
    </div>
  );
}


/* ─── Pure sub-components ───────────────────────────────────────────────── */

function SectionTitle({ title, subtitle, right }) {
  return (
    <div className="flex items-end justify-between mb-3">
      <div>
        <h3 className="text-sm font-semibold tracking-tight">{title}</h3>
        {subtitle && <p className="text-xs text-foreground/55 mt-0.5">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

function Kpi({ label, value, hint, icon, tone, testid }) {
  const toneCls = tone === "good"
    ? "bg-emerald-50 border-emerald-200 text-emerald-900"
    : tone === "warn"
    ? "bg-rose-50 border-rose-200 text-rose-900"
    : tone === "ink"
    ? "bg-foreground text-background border-foreground"
    : "bg-background";
  return (
    <div className={`rounded-2xl border p-3 ${toneCls}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-70">
        {icon}<span>{label}</span>
      </div>
      <div className="mt-1.5 text-xl font-semibold tabular-nums">{value}</div>
      {hint && <div className="text-[10px] opacity-65 mt-0.5">{hint}</div>}
    </div>
  );
}

function TreasuryCard({ treasury, onSweep, onAddMethod, onRefresh }) {
  if (!treasury) return null;
  const t = treasury.summary || {};
  const pms = treasury.payment_methods || [];
  const tx  = treasury.transactions || [];

  return (
    <div className="rounded-2xl border bg-background overflow-hidden" data-testid="treasury-card">
      <div className="bg-gradient-to-br from-foreground to-foreground/80 text-background px-5 py-4 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider opacity-65 flex items-center gap-1.5">
            <Wallet className="w-3.5 h-3.5"/>verqo Treasury · FBO sub-ledger
          </div>
          <div className="mt-1 text-3xl font-bold tabular-nums">{money(t.balance)}</div>
          <div className="text-xs opacity-75 mt-0.5">
            Lifetime in {money(t.lifetime_in)} · out {money(t.lifetime_out)} · {t.tx_count} txns
            {t.last_sweep_at && (
              <> · last sweep {money(t.last_sweep_amount)} {t.last_sweep_status}</>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={onSweep}
            disabled={!t.balance || pms.length === 0}
            data-testid="treasury-sweep"
          >
            <ArrowDownToLine className="w-4 h-4 mr-1.5"/>
            Sweep to bank
          </Button>
          <Button size="sm" variant="ghost" className="text-background hover:text-foreground" onClick={onRefresh}>
            <RefreshCw className="w-4 h-4"/>
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x">
        {/* Payment methods */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground/65">verqo bank / card accounts</h4>
            <Button size="sm" variant="ghost" onClick={onAddMethod} data-testid="add-treasury-method">
              <Plus className="w-3.5 h-3.5 mr-1"/>Add
            </Button>
          </div>
          {pms.length === 0 ? (
            <div className="text-xs text-foreground/55 p-4 text-center border border-dashed rounded-lg">
              No corporate bank linked yet — add verqo's operating account to enable withdrawals.
            </div>
          ) : (
            <ul className="space-y-1.5">
              {pms.map(pm => (
                <li key={pm.id} className="flex items-center justify-between text-xs border rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2">
                    {pm.type === "card"
                      ? <CreditCard className="w-3.5 h-3.5 text-foreground/55"/>
                      : <Building2 className="w-3.5 h-3.5 text-foreground/55"/>}
                    <div>
                      <div className="font-medium">{pm.label}</div>
                      <div className="text-foreground/55 text-[10px]">
                        {pm.rails} · ···{pm.last4}
                        {pm.is_default && <span className="ml-1.5 inline-flex items-center gap-0.5 text-emerald-700"><Star className="w-3 h-3 fill-emerald-700"/>default</span>}
                      </div>
                    </div>
                  </div>
                  <DeleteMethodBtn id={pm.id} onDone={onRefresh}/>
                </li>
              ))}
            </ul>
          )}
        </div>
        {/* Recent treasury txns */}
        <div className="p-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground/65 mb-2">Recent treasury movements</h4>
          {tx.length === 0 ? (
            <div className="text-xs text-foreground/55 p-4 text-center border border-dashed rounded-lg">No movements yet.</div>
          ) : (
            <ul className="space-y-1 max-h-48 overflow-y-auto">
              {tx.slice(0, 12).map(t2 => (
                <li key={t2.id} className="flex items-center justify-between text-xs py-1 border-b last:border-0">
                  <div className="flex items-center gap-2 min-w-0">
                    {t2.amount > 0
                      ? <ArrowUpRight className="w-3 h-3 text-emerald-700 flex-shrink-0"/>
                      : <ArrowDownRight className="w-3 h-3 text-rose-700 flex-shrink-0"/>}
                    <span className="text-foreground/85 truncate">{t2.memo}</span>
                  </div>
                  <span className={`tabular-nums font-medium ml-2 ${t2.amount > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                    {t2.amount > 0 ? "+" : ""}{money(t2.amount)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function DeleteMethodBtn({ id, onDone }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    if (!window.confirm("Delete this payment method? Pending sweeps will still settle to it.")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/treasury/payment-methods/${id}`);
      toast.success("Removed");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    } finally { setBusy(false); }
  };
  return (
    <button onClick={onClick} disabled={busy} className="text-foreground/40 hover:text-rose-600 transition" title="Delete">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Trash2 className="w-3.5 h-3.5"/>}
    </button>
  );
}

function StreamTile({ stream }) {
  const Icon = SOURCE_ICON[stream.source] || DollarSign;
  const tone = SOURCE_TONE[stream.source] || "from-foreground/5 to-foreground/10 text-foreground border-foreground/15";
  return (
    <div className={`rounded-2xl border bg-gradient-to-br ${tone} p-4`} data-testid={`stream-${stream.source}`}>
      <div className="flex items-center gap-2 text-xs font-medium">
        <div className="w-7 h-7 rounded-full bg-white/70 flex items-center justify-center">
          <Icon className="w-3.5 h-3.5"/>
        </div>
        <span>{stream.label}</span>
      </div>
      <div className="mt-3 text-xl font-bold tabular-nums">{moneyShort(stream.lifetime.fee)}</div>
      <div className="text-[11px] opacity-75 mt-0.5">Lifetime · {stream.lifetime.count} txns</div>
      <div className="text-[11px] mt-2 flex justify-between">
        <span className="opacity-75">MTD</span>
        <span className="tabular-nums font-semibold">{moneyShort(stream.mtd.fee)}</span>
      </div>
      <div className="text-[11px] flex justify-between">
        <span className="opacity-75">YTD</span>
        <span className="tabular-nums font-semibold">{moneyShort(stream.ytd.fee)}</span>
      </div>
    </div>
  );
}

function StackedChart({ series }) {
  const data = series?.months || [];
  const sources = series?.sources || [];
  const max = useMemo(() => Math.max(1, ...data.map(d => d.total)), [data]);

  if (data.length === 0) {
    return (
      <div className="rounded-2xl border bg-background p-8 text-center text-foreground/55 text-sm">
        No revenue data in the last 12 months.
      </div>
    );
  }
  return (
    <div className="rounded-2xl border bg-background p-4" data-testid="revenue-chart">
      <div className="flex items-end gap-2 h-52">
        {data.map(d => {
          const sliceHeights = sources.map(src => ({
            src,
            h: max > 0 ? ((d[src] || 0) / max) * 100 : 0,
            v: d[src] || 0,
          })).filter(s => s.h > 0);
          return (
            <div key={`${d.year}-${d.month}`} className="flex-1 flex flex-col items-center group">
              <div className="text-[10px] font-medium tabular-nums opacity-0 group-hover:opacity-100 transition">
                {moneyShort(d.total)}
              </div>
              <div className="w-full h-44 flex flex-col-reverse rounded-md overflow-hidden bg-foreground/5">
                {sliceHeights.map(s => (
                  <div
                    key={s.src}
                    style={{ height: `${s.h}%`, backgroundColor: SOURCE_BAR_COLOR[s.src] || "#94a3b8" }}
                    title={`${s.src}: ${money(s.v)}`}
                  />
                ))}
              </div>
              <div className="text-[10px] text-foreground/55 mt-1">{d.label.split(" ")[0]}</div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1.5 mt-4 text-[10px]">
        {sources.map(s => (
          <div key={s} className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: SOURCE_BAR_COLOR[s] || "#94a3b8" }}/>
            <span className="text-foreground/70">{s.replace(/_/g, " ")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TopSellersTable({ rows }) {
  if (rows.length === 0) {
    return <div className="rounded-2xl border bg-background p-6 text-center text-foreground/55 text-sm">No sellers attributed yet.</div>;
  }
  return (
    <div className="rounded-2xl border bg-background overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-foreground/55 border-b">
            <th className="px-4 py-2 font-medium">#</th>
            <th className="px-4 py-2 font-medium">Seller</th>
            <th className="px-4 py-2 font-medium text-right">Revenue contributed</th>
            <th className="px-4 py-2 font-medium text-right">Gross processed</th>
            <th className="px-4 py-2 font-medium text-right">Txns</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.seller_id} className="border-b last:border-0 hover:bg-foreground/3" data-testid={`top-seller-${r.seller_id}`}>
              <td className="px-4 py-2.5 text-foreground/55 tabular-nums">{i + 1}</td>
              <td className="px-4 py-2.5">
                <div className="font-medium">{r.seller_name}</div>
                <div className="text-[10px] text-foreground/55">{r.email}</div>
              </td>
              <td className="px-4 py-2.5 text-right font-semibold tabular-nums">{money(r.fee)}</td>
              <td className="px-4 py-2.5 text-right text-foreground/70 tabular-nums">{moneyShort(r.gross)}</td>
              <td className="px-4 py-2.5 text-right text-foreground/70 tabular-nums">{r.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LedgerFilters({ source, setSource, query, setQuery, from, setFrom, to, setTo, onApply, onClear }) {
  const sources = [
    "dropship","factoring","insurance","shipping",
    "wallet_topup_card","wallet_topup_ach","wallet_topup_wire",
    "app_subscription","extension_fee","wallet_float",
  ];
  return (
    <div className="rounded-2xl border bg-background p-3 mb-3 flex flex-wrap items-end gap-2">
      <div className="flex-1 min-w-[200px]">
        <Label className="text-[10px] uppercase tracking-wider text-foreground/55">Search</Label>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40"/>
          <Input
            data-testid="ledger-search"
            className="pl-8 h-9"
            placeholder="memo or ref"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onApply()}
          />
        </div>
      </div>
      <div className="w-[180px]">
        <Label className="text-[10px] uppercase tracking-wider text-foreground/55">Source</Label>
        <Select value={source || "all"} onValueChange={(v) => setSource(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="ledger-source-filter" className="h-9"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            {sources.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-foreground/55">From</Label>
        <Input type="date" className="h-9 w-[140px]" value={from} onChange={(e) => setFrom(e.target.value)}/>
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-foreground/55">To</Label>
        <Input type="date" className="h-9 w-[140px]" value={to} onChange={(e) => setTo(e.target.value)}/>
      </div>
      <Button size="sm" onClick={onApply} data-testid="ledger-apply">Apply</Button>
      <Button size="sm" variant="ghost" onClick={onClear}>Clear</Button>
    </div>
  );
}

function LedgerTable({ rows }) {
  if (rows.length === 0) {
    return <div className="rounded-2xl border bg-background p-8 text-center text-foreground/55 text-sm">No rows match.</div>;
  }
  return (
    <div className="rounded-2xl border bg-background overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-foreground/55 border-b">
            <th className="px-3 py-2 font-medium">Collected</th>
            <th className="px-3 py-2 font-medium">Source</th>
            <th className="px-3 py-2 font-medium">Ref</th>
            <th className="px-3 py-2 font-medium">Seller / Buyer</th>
            <th className="px-3 py-2 font-medium text-right">Gross</th>
            <th className="px-3 py-2 font-medium text-right">Fee</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const Icon = SOURCE_ICON[r.source] || DollarSign;
            return (
              <tr key={r.id} className="border-b last:border-0 hover:bg-foreground/3">
                <td className="px-3 py-2 text-[11px] text-foreground/65 whitespace-nowrap">
                  {r.collected_at ? new Date(r.collected_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—"}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <span className="w-5 h-5 rounded flex items-center justify-center" style={{ backgroundColor: `${SOURCE_BAR_COLOR[r.source]}22` }}>
                      <Icon className="w-3 h-3" style={{ color: SOURCE_BAR_COLOR[r.source] }}/>
                    </span>
                    <span className="text-[11px]">{r.label || r.source}</span>
                  </div>
                </td>
                <td className="px-3 py-2 font-mono text-[10px] text-foreground/65">{r.ref_code || (r.ref_id || "").slice(0, 10)}</td>
                <td className="px-3 py-2 text-[11px]">
                  {r.seller_name && <div>{r.seller_name}</div>}
                  {r.buyer_name && <div className="text-foreground/55">↳ {r.buyer_name}</div>}
                  {!r.seller_name && !r.buyer_name && <span className="text-foreground/40">—</span>}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-foreground/70">{money(r.gross)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700">
                  +{money(r.fee_amount)}
                  {r.fee_pct ? <span className="text-[10px] text-foreground/45 ml-1">({pct(r.fee_pct, 1)})</span> : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SweepDialog({ open, onOpenChange, treasury, onDone }) {
  const [amount, setAmount] = useState("");
  const [methodId, setMethodId] = useState("");
  const [memo, setMemo] = useState("");
  const [busy, setBusy] = useState(false);

  const summary = treasury?.summary || {};
  const pms = treasury?.payment_methods || [];

  useEffect(() => {
    if (open) {
      const def = pms.find(p => p.is_default) || pms[0];
      setMethodId(def?.id || "");
      setAmount("");
      setMemo("");
    }
  }, [open, pms]);

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0)          { toast.error("Enter an amount"); return; }
    if (amt > (summary.balance || 0)) { toast.error("Exceeds treasury balance"); return; }
    if (!methodId)                  { toast.error("Pick a destination"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/treasury/withdraw", { amount: amt, method_id: methodId, memo });
      toast.success(`Swept ${money(r.data.txn.amount * -1)} to bank · ${r.data.txn.ref}`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sweep failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="sweep-dialog">
        <DialogHeader>
          <DialogTitle>Sweep treasury to verqo bank</DialogTitle>
          <DialogDescription>
            Pull funds from verqo's platform sub-ledger (FBO account) to a corporate bank.
            Available: <span className="font-semibold text-foreground">{money(summary.balance)}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="amt">Amount (USD)</Label>
            <Input
              id="amt" type="number" min="0.01" step="0.01"
              placeholder="e.g. 25000"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              data-testid="sweep-amount"
            />
            <div className="flex justify-end mt-1">
              <button
                type="button"
                className="text-[10px] text-foreground/55 hover:text-foreground"
                onClick={() => setAmount(String(summary.balance || 0))}
              >Sweep entire balance ({money(summary.balance)})</button>
            </div>
          </div>
          <div>
            <Label>Destination</Label>
            <Select value={methodId} onValueChange={setMethodId}>
              <SelectTrigger data-testid="sweep-method"><SelectValue placeholder="Pick a method"/></SelectTrigger>
              <SelectContent>
                {pms.map(p => <SelectItem key={p.id} value={p.id}>{p.label} ({p.rails})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="memo">Memo (optional)</Label>
            <Input id="memo" placeholder="e.g. Monthly operating distribution" value={memo} onChange={(e) => setMemo(e.target.value)}/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}><X className="w-4 h-4 mr-1"/>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="sweep-submit">
            {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>}
            <ArrowDownToLine className="w-4 h-4 mr-1.5"/>
            Sweep
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddMethodDialog({ open, onOpenChange, onDone }) {
  const [type, setType] = useState("bank");
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setType("bank"); setForm({}); }
  }, [open]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const body = { type, ...form };
      await api.post("/admin/treasury/payment-methods", body);
      toast.success("Method added");
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="add-method-dialog">
        <DialogHeader>
          <DialogTitle>Add verqo bank / card</DialogTitle>
          <DialogDescription>
            Where to send funds when sweeping the treasury sub-ledger. Numbers are encrypted at rest.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="bank">Bank account (ACH / wire)</SelectItem>
                <SelectItem value="card">Card</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Label (optional)</Label>
            <Input placeholder="e.g. Mercury Operating" value={form.label || ""} onChange={(e) => set("label", e.target.value)}/>
          </div>
          {type === "bank" ? (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Bank name</Label>
                  <Input placeholder="Mercury / JPM / …" value={form.bank_name || ""} onChange={(e) => set("bank_name", e.target.value)} data-testid="bank-name"/>
                </div>
                <div>
                  <Label>Account holder</Label>
                  <Input placeholder="verqo, Inc." value={form.account_holder || ""} onChange={(e) => set("account_holder", e.target.value)}/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Account #</Label>
                  <Input placeholder="9-digit+" value={form.account_number || ""} onChange={(e) => set("account_number", e.target.value)} data-testid="bank-account"/>
                </div>
                <div>
                  <Label>Routing # (ACH)</Label>
                  <Input placeholder="9 digits" value={form.routing_number || ""} onChange={(e) => set("routing_number", e.target.value)} data-testid="bank-routing"/>
                </div>
              </div>
              <div>
                <Label>Account type</Label>
                <Select value={form.account_type || ""} onValueChange={(v) => set("account_type", v)}>
                  <SelectTrigger><SelectValue placeholder="checking"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="checking">Checking</SelectItem>
                    <SelectItem value="savings">Savings</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Brand</Label>
                  <Input placeholder="Visa" value={form.brand || ""} onChange={(e) => set("brand", e.target.value)}/>
                </div>
                <div>
                  <Label>Last 4</Label>
                  <Input maxLength={4} placeholder="1234" value={form.last4 || ""} onChange={(e) => set("last4", e.target.value)}/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Exp (MM/YY)</Label>
                  <Input placeholder="12/29" value={form.exp || ""} onChange={(e) => set("exp", e.target.value)}/>
                </div>
                <div>
                  <Label>Cardholder name</Label>
                  <Input placeholder="verqo, Inc." value={form.cardholder_name || ""} onChange={(e) => set("cardholder_name", e.target.value)}/>
                </div>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}><X className="w-4 h-4 mr-1"/>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="add-method-submit">
            {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>}
            <Plus className="w-4 h-4 mr-1"/>
            Add method
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
